<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import { api } from "../api/Api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import {
  formatHumanDate,
  formatNumber,
  isEmpty,
} from "../utils/CommonFunctions";
import ReportsMenu from '../components/reports/ReportsMenu.vue'
import { TableField } from "../components/table/table";
import { InvoiceList } from "../types/models";
import DataTable from "../components/table/DataTable.vue";
import AsyncImage from "../components/ui/form/AsyncImage.vue";
import { Chart, ChartConfiguration, ArcElement, DoughnutController } from 'chart.js';
import { makeCenterTextPlugin } from '../plugins/centerTextPlugin.js';
import { useScreenSize } from "../utils/useScreenSize.js";
import IconComponent from "../components/ui/common/IconComponent.vue";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  LineController,
  Tooltip,
  Legend,
  ArcElement, 
  DoughnutController,
  Filler
);

const { isMobile } = useScreenSize();

const outlets = ref<any[]>([])

// For dynamic refs
const gaugeRefs = ref<(HTMLCanvasElement | null)[]>([])

function setGaugeRef(idx: number) {
  return (el: Element | null) => {
    gaugeRefs.value[idx] = el as HTMLCanvasElement | null;
  };
}

function getBreakevenPercent(outlet: { total_cost: any; total_expenses: any; total_sales: number; }) {
  const gross_profit = outlet.total_sales - outlet.total_cost;
  if (outlet.total_expenses === 0) return 100;
  if (outlet.total_sales === 0) return 0;
  if (gross_profit <= 0) return 0;
  console.log(gross_profit, outlet.total_cost, (gross_profit / outlet.total_expenses) * 100)
  return Math.round((gross_profit / outlet.total_expenses) * 100);
}

function getNetProfit(outlet: { total_cost: any; total_expenses: any; total_sales: number; }) {
  return Number(outlet.total_sales) - (Number(outlet.total_cost) + Number(outlet.total_expenses))
}

const router = useRouter();

const filteredInvoices = ref<InvoiceList[]>([]);
const invoices = ref<InvoiceList[]>([]);
const recordsPerPage = ref(10);
const currentPage = ref(1);

const invoiceFields: TableField[] = [
  { name: "date_time", caption: "Date", sortable: false, fieldType: "date" },  
  { name: "customer", caption: "Customer", sortable: false, fieldType: "text" },
  { name: "sales_rep", caption: "Rep", sortable: false, fieldType: "text" },
  { name: "due_days", caption: "Due Days", sortable: false, fieldType: "text" },
  {
    name: "invoice_amount",
    caption: "Invoice Amount",
    sortable: false,
    fieldType: "number",
  },
  { name: "due_date", caption: "Due Date", sortable: false, fieldType: "date" },  
  { name: "document_no", caption: "Invoice #", sortable: false, fieldType: "text" },  
  { name: "paid_amount", caption: "Paid Amount", sortable: false, fieldType: "number" },
];

const loadMorePendingInvoices = () => {
  const start = (currentPage.value - 1) * recordsPerPage.value;
  const end = currentPage.value * recordsPerPage.value;
  let inv = invoices.value.slice(start, end);
  inv.forEach((i) => {
    filteredInvoices.value.push(i);
  });
  currentPage.value++;
};

const dashboard = ref();
onMounted(async () => {
  try {
    const response = await api.post("/clients/dashboard");
    if (response.success == true) {
      dashboard.value = response.data;
      invoices.value = response.data.pending_invoices;
      if (response.data && response.data.outlet_data) {
        outlets.value = response.data.outlet_data;
        await setOutletBreakeven();
      }
      nextTick(() => {
        if (response.data && response.data.dailysales_trend) {
        createDailySalesTrend(response.data.dailysales_trend.labels, response.data.dailysales_trend.last, response.data.dailysales_trend.current);
      }
      if (response.data && response.data.monthlysales_trend) {
        createMonthSalesTrend(response.data.monthlysales_trend.labels, response.data.monthlysales_trend.data);
      }
      });
      loadMorePendingInvoices();
    }
  } catch (error) {
    // Handle unauthorized access
    router.push("/");
  }
});

const setOutletBreakeven = async () => {
  await nextTick()
  outlets.value.forEach((outlet, idx) => {
    const netProfit = getBreakevenPercent(outlet)
    const percent = getBreakevenPercent(outlet)
    console.log(percent/1000)
    const color = netProfit < 0 ? '#e74c3c' : '#2ecc71'
    if (gaugeRefs.value[idx]) {
      new Chart(gaugeRefs.value[idx], {
        type: 'doughnut',
        data: {
          datasets: [{
        data: [percent / 100, 1 - percent / 100],
        backgroundColor: [color, '#eee'],
        borderWidth: 0,
        circumference: 270,
        rotation: 225,
          }]
        },
        options: {
          responsive: false,
          cutout: '70%',
          plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
          }
        },
        plugins: [makeCenterTextPlugin(outlet.name, `${percent}%`)]
      })
    }
  })
};

const getDeliveryStatus = (delivery: any) => {
  if (!isEmpty(delivery.delivered_on)) return "Delivered";
  else if (delivery.dispatched == true) return "Dispatched";
  else if (!isEmpty(delivery.assigned_to_users_id)) return "Delivery Assigned";
  else return "Payment Completed";
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "Payment Completed":
      return "bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "Dispatched":
      return "bg-orange-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    case "Delivery Assigned":
      return "bg-yellow-200 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    default:
      return "bg-blue-200 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
  }
};

const calculateProgress = (sales: number, target: number) => {
  return (sales / target) * 100;
};



// Reactive reference for the chart instance
let dailySalesTrendChart: Chart | null = null;
const createDailySalesTrend = (labels: string[], lastMonthData: number[], currentMonthData: number[]) => {
  // Get the canvas context
  const ctx = (document.getElementById('dailySalesTrendChart') as HTMLCanvasElement).getContext('2d');
  if (ctx && !dailySalesTrendChart) {
    // Chart configuration
    const chartConfig: ChartConfiguration = {
      type: 'line',
      data: {
        labels: labels, // X-Axis: Dates
        datasets: [
          {
            label: 'Last Month',
            data: lastMonthData, // Y-Axis: Last Month's Sales
            borderColor: 'rgba(255, 99, 132, 1)',
            backgroundColor: 'rgba(255, 99, 132, 0.2)',
            borderWidth: 2,
            fill: true,
          },
          {
            label: 'Current Month',
            data: currentMonthData, // Y-Axis: Current Month's Sales
            borderColor: 'rgba(54, 162, 235, 1)',
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            borderWidth: 2,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: true, // Enable the title display
                text: 'Daily Sales Trend', // Title content
                font: {
                    size: 18, // Font size for the title
                },
                padding: {
                    top: 10, // Padding above the title
                    bottom: 10, // Padding below the title
                },
            },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Total Sales',
            },
          },
          x: {
            title: {
              display: true,
              text: 'Dates',
            },
          },
        },
      },
    };

    // Create the chart instance
    dailySalesTrendChart = new Chart(ctx, chartConfig);
  }
};


let monthlySalesTrendChart: Chart | null = null;
const createMonthSalesTrend = (labels: string[], data: number[]) => {
  // Get the canvas context
  const ctx = (document.getElementById('monthlySalesTrendChart') as HTMLCanvasElement).getContext('2d');
  if (ctx && !monthlySalesTrendChart) {
    // Chart configuration
    const chartConfig: ChartConfiguration = {
      type: 'line',
      data: {
        labels: labels, // X-Axis: Dates
        datasets: [
          {
            label: 'Monthly Sales',
            data: data, // Y-Axis: Last Month's Sales
            borderColor: 'rgba(255, 99, 132, 1)',
            backgroundColor: 'rgba(255, 99, 132, 0.2)',
            borderWidth: 2,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
            title: {
                display: true, // Enable the title display
                text: 'Monthly Sales Trend', // Title content
                font: {
                    size: 18, // Font size for the title
                },
                padding: {
                    top: 10, // Padding above the title
                    bottom: 10, // Padding below the title
                },
            },
        },
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Total Sales',
            },
          },
          x: {
            title: {
              display: true,
              text: 'Month',
            },
          },
        },
      },
    };

    // Create the chart instance
    monthlySalesTrendChart = new Chart(ctx, chartConfig);
  }
};

</script>

<template>
  <div class="space-y-6">
    <!-- Quick Stats -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-6" v-if="dashboard && dashboard.progress">
      <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Total Sales</p>
            <h3 class="text-2xl font-semibold text-gray-900 dark:text-white">
              {{ formatNumber(dashboard.progress.current.sales) }}
            </h3>
          </div>
          <font-awesome-icon icon="sack-dollar" class="text-3xl" />
        </div>
        <div class="mt-4 flex items-center text-sm">
          <span
            :class="{
              'text-green-500': dashboard.progress.change.sales >= 0,
              'text-red-500': dashboard.progress.change.sales < 0,
            }"
            ><span v-if="dashboard.progress.change.sales >= 0">↑</span>
            <span v-else>↓</span>
            {{ formatNumber(dashboard.progress.change.sales) }}%</span
          >
          <span class="ml-2 text-gray-500 dark:text-gray-400"
            >vs last month</span
          >
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Orders</p>
            <h3 class="text-2xl font-semibold text-gray-900 dark:text-white">
              {{ formatNumber(dashboard.progress.current.invoices) }}
            </h3>
          </div>
          <font-awesome-icon icon="boxes-packing" class="text-3xl" />
        </div>
        <div class="mt-4 flex items-center text-sm">
          <span
            :class="{
              'text-green-500': dashboard.progress.change.invoices >= 0,
              'text-red-500': dashboard.progress.change.invoices < 0,
            }"
            ><span v-if="dashboard.progress.change.invoices >= 0">↑</span>
            <span v-else>↓</span>
            {{ formatNumber(dashboard.progress.change.invoices) }}%</span
          >
          <span class="ml-2 text-gray-500 dark:text-gray-400"
            >vs last month</span
          >
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Customers</p>
            <h3 class="text-2xl font-semibold text-gray-900 dark:text-white">
              {{ formatNumber(dashboard.progress.current.customers) }}
            </h3>
          </div>
          <font-awesome-icon icon="people-arrows" class="text-3xl" />
        </div>
        <div class="mt-4 flex items-center text-sm">
          <span
            :class="{
              'text-green-500': dashboard.progress.change.customers >= 0,
              'text-red-500': dashboard.progress.change.customers < 0,
            }"
            ><span v-if="dashboard.progress.change.customers >= 0">↑</span>
            <span v-else>↓</span>
            {{ formatNumber(dashboard.progress.change.customers) }}%</span
          >
          <span class="ml-2 text-gray-500 dark:text-gray-400"
            >vs last month</span
          >
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Gross Profit</p>
            <h3 class="text-2xl font-semibold text-gray-900 dark:text-white">
              {{ formatNumber(dashboard.progress.current.profit) }}
            </h3>
          </div>
          <font-awesome-icon icon="chart-simple" class="text-3xl" />
        </div>
        <div class="mt-4 flex items-center text-sm">
          <span
            :class="{
              'text-green-500': dashboard.progress.change.profit >= 0,
              'text-red-500': dashboard.progress.change.profit < 0,
            }"
            ><span v-if="dashboard.progress.change.profit >= 0">↑</span>
            <span v-else>↓</span>
            {{ formatNumber(dashboard.progress.change.profit) }}%</span
          >
          <span class="ml-2 text-gray-500 dark:text-gray-400"
            >vs last month</span
          >
        </div>
      </div>
    </div>

    <!-- Sales Trends (Daily/Monthly)-->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6" v-if="(dashboard && dashboard.dailysales_trend) || (dashboard && dashboard.monthlysales_trend)">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6" v-if="dashboard.dailysales_trend">
        <canvas id="dailySalesTrendChart" style="height: 300px; width: 100%;"></canvas>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6" v-if="dashboard.monthlysales_trend">
      <canvas id="monthlySalesTrendChart" style="height: 300px; width: 100%;"></canvas>
      </div>
    </div>

    <!-- Breakeven -->
    <div class="md:flex justify-center gap-6" :class="{'w-full' : isMobile}" v-if="outlets && outlets.length > 0">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 text-center"
         v-for="(outlet, idx) in outlets" 
         :key="outlet.name" 
         :style="{ width: isMobile ? '100%' : '210px' }">
      <div class="flex justify-center">
        <canvas 
          :ref="setGaugeRef(idx) as any" 
          :width="isMobile ? '300' : '180'" 
          :height="isMobile ? '180' : '180'"
          :style="{ display: 'block', margin: isMobile ? '0 auto' : 'inherit' }"
        ></canvas>
      </div>
      <div class="mt-2">
        <div class="ml-2 text-gray-500 dark:text-gray-400 flex"><IconComponent name="Banknote" /> {{ formatNumber(outlet.total_sales) }}</div>
        <div 
          class="ml-2 flex" 
          :class="getNetProfit(outlet) < 0 ? 'text-red-500 dark:text-red-400' : 'text-green-500 dark:text-green-400'"
        >
          <IconComponent name="Coins" tooltip="Net Profit"/> {{ formatNumber(getNetProfit(outlet)) }}
        </div>
        <div class="ml-2 text-gray-500 dark:text-gray-400 flex "><IconComponent name="Scale" /> {{ getBreakevenPercent(outlet) }}%</div>
        <div class="ml-2 text-gray-500 dark:text-gray-400 flex text-sm">Today Sales: {{ formatNumber(outlet.today_sales) }}</div>
      </div>
      </div>
    </div>

    <!-- Top Products and Customers -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6" v-if="(dashboard && dashboard.top & dashboard.top.products) || (dashboard && dashboard.top && dashboard.top.customers)">
      <!-- Top Products -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            Top Products
          </h3>
        </div>
        <div class="p-6">
          <div class="space-y-4">
            <div
              v-for="product in dashboard.top.products"
              :key="product.id"
              class="flex items-center justify-between"
            >
              <div class="flex items-center gap-4">
                <div
                  class="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center"
                >
                  <AsyncImage
                    :src="product.photo"
                    :alt="product.name" />
                </div>
                <div>
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    {{ product.item }}
                  </h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ product.current_month_invoices }} Invoices
                  </p>
                </div>
              </div>
              <div class="text-right">
                <p class="font-medium text-gray-900 dark:text-white">
                  {{ formatNumber(product.total_sales) }}
                </p>
                <p
                  class="text-sm"
                  :class="
                    product.growth >= 0 ? 'text-green-500' : 'text-red-500'
                  "
                >
                  {{ product.growth >= 0 ? "↑" : "↓" }}
                  {{ formatNumber(Math.abs(product.growth)) }}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Customers -->
      <div
        class="bg-white dark:bg-gray-800 rounded-lg shadow-sm"
        v-if="dashboard"
      >
        <div class="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            Top Customers
          </h3>
          <ReportsMenu :reports="dashboard?.customer_reports"  id="dashboard-report"/> <!-- TODO: check for permissions -->
        </div>
        <div class="p-6">
          <div class="space-y-4">
            <div
              v-for="customer in dashboard.top.customers"
              :key="customer.id"
              class="flex items-center justify-between"
            >
              <div class="flex items-center gap-4">
                <div>
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    {{ customer.name }}
                  </h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ customer.customer_type }}
                  </p>
                </div>
              </div>
              <div class="text-right">
                <p class="font-medium text-gray-900 dark:text-white">
                  {{ formatNumber(customer.total_sales) }}
                </p>
                <p class="font-sm text-gray-500 dark:text-white">
                  {{ formatNumber(customer.invoice_count, 0) }} Invoices
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sales Performance -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6" v-if="(dashboard && dashboard.top && dashboard.top.reps) || (dashboard && dashboard.pending_invoices)">
      <!-- Top Sales Persons -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            Sales Performance
          </h3>
          <ReportsMenu :reports="dashboard?.customer_reports"  id="dashboard-report"/> <!-- TODO: check for permissions -->
        </div>
        <div class="p-6">
          <div class="space-y-6">
            <div
              v-for="person in dashboard.top.reps"
              :key="person.id"
              class="space-y-2"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div
                    class="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center text-xl"
                  >
                    {{ person.avatar }}
                  </div>
                  <div>
                    <h4 class="font-medium text-gray-900 dark:text-white">
                      {{ person.name }}
                    </h4>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      {{ formatNumber(person.total_sales) }} /
                      {{ formatNumber(person.monthly_target) }}
                    </p>
                  </div>
                </div>
                <div class="text-right">
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ Math.round((person.total_sales / (person.monthly_target ?? 0)) * 100) }}%
                  </p>
                  <p class="text-sm text-gray-500 dark:text-white">
                    {{ person.invoice_count }} Invoices
                  </p>
                </div>
              </div>
              <div
                class="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden"
              >
                <div
                  class="h-full rounded-full transition-all duration-500"
                  :class="{
                    'bg-red-600':
                      calculateProgress(person.total_sales, 50000) < 30,
                    'bg-orange-600':
                      calculateProgress(person.total_sales, 50000) < 50,
                    'bg-blue-600':
                      calculateProgress(person.total_sales, 50000) < 70,
                    'bg-green-600':
                      calculateProgress(person.total_sales, 50000) >= 70,
                  }"
                  :style="{
                    width: `${formatNumber(
                      calculateProgress(person.total_sales, 50000)
                    )}%`,
                  }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pending Invoices -->
      <div
        class="bg-white dark:bg-gray-800 rounded-lg shadow-sm"
        v-if="dashboard && dashboard.pending_invoices"
      >
        <div class="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            Customer Due
          </h3>
          <ReportsMenu :reports="dashboard?.customer_reports"  id="dashboard-report"/> <!-- TODO: check for permissions -->
        </div>
        <div class="p-6">
          <div class="space-y-4">
            <div class="">
              <div>
                <DataTable
                  :fields="invoiceFields"
                  table-width="300"
                  :rows="filteredInvoices"
                  :show-actions="false"
                  title="Due Invoices"
                  :allow-edit="false"
                  :showHeader="false"
                  :allow-disable="false"
                  :show-quick-view="false"
                  :showShareActions="false"
                  :remote="true"
                  :actions="[]"
                  :endPoint="''"
                  :showPagination="false"
                  :show-child-row="false"
                />
                <div
                  class="flex items-center justify-center text-xl"
                  v-if="filteredInvoices && invoices && filteredInvoices.length < invoices.length"
                >
                  <button
                    @click="loadMorePendingInvoices"
                    class="px-2 md:px-4 py-2 text-sm font-medium text-white flex items-center gap-1 bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    <span class="text-[11px] uppercase tracking-wide mt-1"
                      >Load More</span
                    >
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pending Deliveries -->
    <div
      class="bg-white dark:bg-gray-800 rounded-lg shadow-sm"
      v-if="dashboard && dashboard.deliverys"
    >
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Pending Deliveries
        </h3>
      </div>
      <div class="p-6">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="delivery in dashboard.deliverys"
            :key="delivery.id"
            class="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
          >
            <div class="flex justify-between items-start mb-2">
              <div>
                <h4 class="font-medium text-gray-900 dark:text-white">
                  Order No: {{ delivery.document_no }}
                </h4>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ delivery.customer }}
                </p>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ delivery.mobile }}
                </p>
              </div>
              <div class="text-right">
                <p
                  class="px-2 py-1 text-xs font-medium rounded-full dark:text-white"
                  :class="getStatusColor(delivery)"
                >
                  {{ getDeliveryStatus(delivery) }}
                </p>
                <p class="text-sm text-gray-500 dark:text-white">
                  {{ formatHumanDate(delivery.order_date) }}
                </p>
              </div>
            </div>
            <div class="flex justify-between items-center mt-4">
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Ordered On: {{ delivery.order_date }}
              </p>
              <p class="font-medium text-gray-900 dark:text-white">
                Deliver On: {{ delivery.delivery_date }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
